import React, { useEffect } from 'react'

import { useEditAlarm } from '../hooks/useEditAlarm'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'
import { SubmitHandler, useForm } from 'react-hook-form'

import { Box, GridItem } from '@chakra-ui/react'

import {
  Button,
  FormInputControl,
  FormInputSelectControl,
  SkeletonModalBody,
} from '@/components/ui'

import { EditDeviceModalSection } from '../../components/EditDeviceModalSection'
import { FourColumnGrid } from '../../components/FourColumnGrid'
import { SectionHeader } from '../../components/SectionHeader'
import { TwoColumnGrid } from '../../components/TwoColumnGrid'
import { AlarmDataTypeI } from '../../types/types'
import { isPlaced } from '../../utils/utils'
import { getAlarmInputs } from '../utils/utils'

interface EditAlarmFormIProps {
  deviceId: string
  onLoadingChange: (loading: boolean) => void
}

export const EditAlarmForm = ({
  deviceId,
  onLoadingChange,
}: EditAlarmFormIProps) => {
  const { shouldEnableUpdateDevice } = usePermissions()
  const isEventPromotion = useFeatureFlag(FeatureFlag.useEventPromotionAlarms)
  const {
    alarmData,
    isLoading,
    isUpdateDeviceLoading,
    isUpdateAlarmLoading,
    floorsOptions,
    incidentTypesOptions,
    isIncidentTypesLoading,
    updateAlarm,
  } = useEditAlarm({
    deviceId,
  })
  const form = useForm<AlarmDataTypeI>({
    mode: 'onChange',
  })
  const {
    handleSubmit,
    errors,
    register,
    control,
    setValue,
    reset,
    formState: { dirtyFields, isDirty },
  } = form
  const { showSuccess, showError } = useToasts()

  /**
   * Handles form submission for alarm updates
   * Prepares device and alarm data, then calls the update function
   */
  const onSubmit: SubmitHandler<AlarmDataTypeI> = async (values) => {
    const formData = {
      id: deviceId,
      name: values.name,
      floorId: values.location?.value,
    }
    const alarmInputData = getAlarmInputs(deviceId, values, dirtyFields)

    try {
      await updateAlarm(formData, alarmInputData, dirtyFields)
      showSuccess('Alarm updated.')
      reset(values)
    } catch (e) {
      Sentry.captureException(e, {
        tags: {
          action: 'update',
          feature: 'edit_alarm',
          surface: 'edit_device_modal',
        },
        extra: {
          deviceId,
          dirtyFields: Object.keys(dirtyFields),
          isAlarmTypeCleared: alarmInputData.alarmTypeId?.value === null,
        },
      })
      showError()
    }
  }

  useEffect(() => {
    // A successful device update refetches DeviceEdit, so this effect re-runs
    // mid-submit. Re-seeding then would overwrite edits the user has not saved
    // yet — and for the Controller-backed alarmTypeId it does so invisibly,
    // leaving the select displaying the cleared value while the form state holds
    // the stale server one, so a retry silently re-sends the old alarm type.
    // Seed only while the form is pristine; `reset` on a successful save clears
    // isDirty and lets the next refetch through.
    if (isDirty) return

    if (!isLoading) {
      setValue('name', alarmData?.device?.name, { shouldValidate: true })
      setValue(
        'location',
        floorsOptions?.find((i) => i.value === alarmData?.device?.floor?.id),
        {
          shouldValidate: true,
        }
      )
      setValue(
        'alarmTypeId',
        // Fall back to null rather than undefined — react-select treats an
        // undefined value as uncontrolled, which would keep displaying the old
        // option after a cleared value is refetched.
        incidentTypesOptions?.find(
          (i) => i.value === alarmData?.device?.alarm?.alarmType?.id
        ) ?? null
      )
      setValue('externalId', alarmData?.device?.alarm?.externalId, {
        shouldValidate: false,
      })
    }
  }, [isLoading, floorsOptions, alarmData, setValue])

  useEffect(() => {
    // Update parent component's loading state
    onLoadingChange(isLoading)
  }, [alarmData, isLoading])

  return (
    <Box>
      {!alarmData || isLoading ? (
        <SkeletonModalBody />
      ) : (
        <EditDeviceModalSection mb={6}>
          <TwoColumnGrid>
            <GridItem>
              <SectionHeader
                subTitle='Configure the basic device information and connections settings for this device '
                title='Device Details & Connection'
              />
            </GridItem>
            {/* @ts-ignore */}
            <GridItem justifySelf='end'>
              <Button
                isDisabled={!shouldEnableUpdateDevice || !isDirty}
                isLoading={isUpdateDeviceLoading || isUpdateAlarmLoading}
                loadingText='Saving Changes'
                onClick={handleSubmit(onSubmit)}
              >
                Save
              </Button>
            </GridItem>
          </TwoColumnGrid>

          <form>
            <FourColumnGrid>
              <GridItem>
                <FormInputControl
                  data-testid='devicesPage_editAlarmModal_name'
                  errorMessage={errors.name && errors.name.message}
                  id='name'
                  inputRef={register({
                    required: 'Device Name is required',
                  })}
                  isInvalid={!!errors.name}
                  label='Device Name'
                  placeholder='Enter the Device Name'
                />
              </GridItem>
              <GridItem>
                <FormInputSelectControl
                  control={control}
                  data-testid='devicesPage_editAlarmModal_location'
                  defaultValue={null}
                  errorMessage='Select a device location'
                  id='location'
                  isClearable
                  isDisabled={isPlaced(alarmData?.device)}
                  isInvalid={!!errors?.location}
                  label='Device Location'
                  options={floorsOptions}
                  placeholder='Select Device Location'
                  rules={{ required: true }}
                  tooltipText={
                    isPlaced(alarmData?.device) &&
                    'Device must not be placed to update device location'
                  }
                />
              </GridItem>
              <GridItem>
                <FormInputControl
                  data-testid='devicesPage_editAlarmModal_externalId'
                  id='externalId'
                  inputRef={register()}
                  label='External ID'
                  placeholder='Alarm External ID'
                />
              </GridItem>
              {isEventPromotion && (
                <GridItem>
                  <FormInputSelectControl
                    control={control}
                    dataTestId='devicesPage_editAlarmModal_incidentType'
                    defaultValue={null}
                    id='alarmTypeId'
                    isClearable
                    isInvalid={!!errors?.alarmTypeId}
                    isLoading={isIncidentTypesLoading}
                    label='Incident Type'
                    options={incidentTypesOptions}
                    placeholder='Select Incident Type'
                  />
                </GridItem>
              )}
            </FourColumnGrid>
          </form>
        </EditDeviceModalSection>
      )}
    </Box>
  )
}
